/* istanbul ignore file */
import React from 'react';
import Promise from 'bluebird';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { BackboneInstanceWrapper } from '@bingads-webui-react/backbone-instance-wrapper';
import { getRouterDep } from '@bingads-webui-campaign-react/legacy-router-dep';
import { EventDelegator } from '@bingads-webui-campaign-react/event-delegator';
import { withSiteMap, processor } from '@bingads-webui-campaign-react/with-site-map';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { ProgressiveLoadingBlock } from '@bingads-webui-campaign-react/shell-ui-common';

export class InjectWrapper extends React.PureComponent {
  constructor(props) {
    super(props);

    const injectViewFactoryOptions = _.omit(props, ['styleScopeName', 'InjectViewFactory', 'InjectViewOption']);

    this.ReactView = React.lazy(() => props.InjectViewFactory(injectViewFactoryOptions)
      .then(InjectedView => Promise.props({ InjectedView, InjectViewOption: props.InjectViewOption }))
      .then(({ InjectedView, InjectViewOption }) => {
        const { deps = {} } = InjectViewOption;
        const viewOptions = {
          ...InjectViewOption,
          deps: {
            router: getRouterDep(props.scope, props.siteMap),
            eventDelegator: new EventDelegator(),
            i18nNewDesign: this.props.i18n,
            ...deps,
          },
        };

        const initData = viewOptions.initData || (() => Promise.resolve());

        return Promise.resolve(new InjectedView(viewOptions))
          .then(view => initData(view).then(() => view))
          .then((view) => {
            props.callbackAfterInjected(view);
            this.instance = view.render();
            return this.instance;
          })
          .then(instance => ({
            default: () => {
              this.props.callbackAfterRendered();
              return (
                <div className={this.props.classes[props.styleScopeName]}>
                  <BackboneInstanceWrapper
                    managed
                    instance={instance}
                  />
                </div>);
            },
          }));
      }));
  }

  componentWillUnmount() {
    if (this.instance && this.instance.remove) {
      this.instance.remove();
    }
  }

  render() {
    return (
      <React.Suspense fallback={<ProgressiveLoadingBlock />}>
        <this.ReactView />
      </React.Suspense>);
  }
}

InjectWrapper.propTypes = {
  InjectViewFactory: PropTypes.func.isRequired,
  serverContext: PropTypes.shape({
    Permissions: PropTypes.object.isRequired,
    CurrentAccount: PropTypes.object.isRequired,
  }).isRequired,
  InjectViewOption: PropTypes.objectOf(PropTypes.any),
  classes: PropTypes.objectOf(PropTypes.any).isRequired,
  styleScopeName: PropTypes.string,
  scope: PropTypes.objectOf(PropTypes.any).isRequired,
  siteMap: PropTypes.arrayOf(PropTypes.any).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  callbackAfterInjected: PropTypes.func,
  callbackAfterRendered: PropTypes.func,
};

InjectWrapper.defaultProps = {
  InjectViewOption: {},
  styleScopeName: 'CampaignCreationSearch',
  callbackAfterInjected: _.noop,
  callbackAfterRendered: _.noop,
};

export const InjectWrapperWithHoC = withI18nConsumer(withSiteMap(InjectWrapper, [processor.processPermissions]));
